/**
 * ET — Article (Pro) JS (drop-in, consolidated)
 * ---------------------------------------------------------------------------
 * Goals (no visual changes):
 * - Performance: single module, single MutationObserver, debounced work
 * - A11y: keyboard-friendly TOC/tabs/anchors, live announcements
 * - Resilience: legacy FAQ upgrader + hostile <summary> shim
 * - Analytics: unified event naming convention: et_* (Shopify CE + dataLayer echo)
 * - Related Articles: hydrated client-side (pinned JSON OR Predictive Search)
 * - Mobile-first: no layout assumptions, avoids heavy work on slow connections
 *
 * HARD RULE:
 * - No Liquid inside this file. Reads window.__ET injected by template.
 *
 * Template prerequisites (Liquid):
 * - Related stub:
 *   <div data-et-related> ... <ul data-et-related-list> ... optional <script data-et-related-pinned>...</script>
 *
 * - window.__ET injected (recommended fields):
 *   window.__ET = { page:'blog', article:{ id, handle, title, url, tags:[], primary_topic:'' }, shop:{...} }
 */

(() => {
  "use strict";

  /* =========================================================================
   * Small utilities (scoped)
   * ========================================================================= */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (el, evt, fn, opts) => el && el.addEventListener(evt, fn, opts);
  const off = (el, evt, fn, opts) => el && el.removeEventListener(evt, fn, opts);

  const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

  const prefersReduced = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

  const slugify = (str) =>
    (str || "")
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const escapeHtml = (str) =>
    (str || "").replace(/[&<>"']/g, (m) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    }[m]));

  const readCSSVar = (name, fallback = "") => {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return v && v.trim() ? v.trim() : fallback;
  };

  const nowMs = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

  // Best-effort connection hints to avoid aggressive prefetch/hydration on slow links.
  const conn = () => navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const canEagerNetwork = () => {
    const c = conn();
    if (!c) return true;
    if (c.saveData) return false;
    const et = (c.effectiveType || "").toLowerCase();
    if (et.includes("2g")) return false;
    return true;
  };

  // Basic once-guard (per page load)
  const once = (key) => {
    window.__ET_FLAGS = window.__ET_FLAGS || {};
    if (window.__ET_FLAGS[key]) return false;
    window.__ET_FLAGS[key] = true;
    return true;
  };

  const getET = () => (window.__ET && typeof window.__ET === "object" ? window.__ET : {});
  const getArticle = () => (getET().article && typeof getET().article === "object" ? getET().article : {});
  const getArticleId = () => getArticle().id || undefined;

  // Locale/format agnostic detection for “FAQ” heading text
  function isFAQHeadingText(text) {
    const s = (text || "")
      .normalize("NFKC")
      .replace(/[^A-Za-z]/g, "")
      .toLowerCase();
    return s === "faq" || s === "frequentlyaskedquestions";
  }

  /* =========================================================================
   * A11y live region (lazy)
   * ========================================================================= */
  let liveRegion;
  function announce(msg) {
    if (!msg) return;
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      Object.assign(liveRegion.style, {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        clipPath: "inset(50%)",
        border: 0,
        whiteSpace: "nowrap",
      });
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = msg;
  }

  async function copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      return true;
    } catch {
      return false;
    }
  }

  function smoothScrollTo(target) {
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.pageYOffset - 4;
    window.scrollTo({ top, behavior: prefersReduced() ? "auto" : "smooth" });
  }

  /* =========================================================================
   * Analytics: Shopify Customer Events + dataLayer echo (et_* naming)
   * ========================================================================= */
  function consentContext() {
    try {
      const canTrack = window.Shopify?.customerPrivacy?.userCanBeTracked?.();
      return { consent: canTrack === true ? "granted" : canTrack === false ? "denied" : "unknown" };
    } catch {
      return { consent: "unknown" };
    }
  }

  function publishEt(eventName, payload = {}) {
    // Always use et_* convention
    const name = String(eventName || "").startsWith("et_") ? String(eventName) : `et_${eventName}`;
    const base = Object.assign(
      {
        ts: Date.now(),
        page: getET().page || "unknown",
        article_id: getArticleId(),
      },
      payload,
      consentContext()
    );

    try {
      // Shopify Customer Events (if available)
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, base);
      }
    } catch { /* no-op */ }

    try {
      // dataLayer echo (GTM/GA4 listeners)
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, base));
      }
    } catch { /* no-op */ }
  }

  function trackClickDelegated() {
    if (!once("et_click_delegated_bound")) return;

    // Delegated click capture for any element exposing data-block-type, product/collection ids, etc.
    document.addEventListener(
      "click",
      (e) => {
        const el = e.target.closest("a[data-block-type], button[data-block-type], a[data-product-id], a[data-collection-id]");
        if (!el) return;

        const blockType = el.getAttribute("data-block-type") || "link";
        const productId = el.getAttribute("data-product-id");
        const collectionId = el.getAttribute("data-collection-id");
        const href = el.getAttribute("href") || undefined;

        publishEt("click", {
          location: blockType,
          href,
          label: (el.textContent || "").trim(),
          product_id: productId ? +productId : undefined,
          collection_id: collectionId ? +collectionId : undefined,
          context: { namespace: "blog_article", location: blockType },
        });
      },
      false
    );
  }

  function trackImpressionsOnce() {
    if (!once("et_impression_observer_bound")) return;

    // One-shot impressions using IntersectionObserver
    let io;
    try {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            if (el.__etSent) return;
            el.__etSent = true;

            const type = el.getAttribute("data-impression");
            if (!type) return;

            publishEt("impression", {
              impression_type: type,
              product_id: +(el.getAttribute("data-product-id") || 0) || undefined,
              product_variant_id: +(el.getAttribute("data-product-variant-id") || 0) || undefined,
              context: { namespace: "blog_article", location: type },
            });
          });
        },
        { threshold: 0.35, rootMargin: "120px 0px" }
      );
    } catch {
      io = null;
    }

    if (!io) return;
    document.querySelectorAll("[data-impression]").forEach((el) => io.observe(el));
  }

  /* =========================================================================
   * Heading ID pass (shared): ensures deterministic, de-duplicated ids
   * ========================================================================= */
  function ensureHeadingIds(body) {
    if (!body) return [];
    const headings = $$("h2, h3", body).filter((h) => (h.textContent || "").trim().length > 0);

    // Exclude headings inside FAQ UI (standard + legacy containers)
    const filtered = headings.filter((h) => !h.closest(".faq-accordion, .et-faq"));

    const seen = new Set();
    filtered.forEach((h, i) => {
      if (!h.id || seen.has(h.id)) {
        let base = slugify(h.textContent) || `section-${i + 1}`;
        let id = base;
        let n = 1;
        while (document.getElementById(id)) id = `${base}-${++n}`;
        h.id = id;
      }
      seen.add(h.id);
    });

    // Respect sticky headers using CSS var (set per theme)
    const anchorOffset = readCSSVar("--eta-anchor-offset", "96px");
    filtered.forEach((h) => {
      h.style.scrollMarginTop = anchorOffset;
    });

    return filtered;
  }

  /* =========================================================================
   * Table of Contents
   * ========================================================================= */
  function buildTOC(section) {
    const toc = $(".et-toc", section);
    const body = $(".et-article-body", section);
    const list = toc && $(".et-toc-list", toc);
    if (!toc || !body || !list) return;

    const headings = ensureHeadingIds(body);
    if (!headings.length) {
      list.replaceChildren();
      toc.setAttribute("hidden", "");
      return;
    }
    toc.removeAttribute("hidden");

    // Build with DOM APIs (avoid string HTML assembly).
    const frag = document.createDocumentFragment();
    headings.forEach((h) => {
      const depth = h.tagName === "H3" ? 3 : 2;
      const li = document.createElement("li");
      li.setAttribute("data-depth", String(depth));

      const a = document.createElement("a");
      a.setAttribute("href", `#${h.id}`);
      a.textContent = (h.textContent || "").trim();

      li.appendChild(a);
      frag.appendChild(li);
    });
    list.replaceChildren(frag);

    const anchors = $$("a", list);
    const setActiveTocLink = (activeHash) => {
      anchors.forEach((a) => {
        a.setAttribute("aria-current", a.getAttribute("href") === activeHash ? "true" : "false");
      });
    };

    // Smooth scroll + history updates
    if (list.__etTocClickHandler) off(list, "click", list.__etTocClickHandler);
    list.__etTocClickHandler = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("#")) return;

      const rawId = href.slice(1);
      const decodedId = decodeURIComponent(rawId);
      let target = document.getElementById(decodedId);
      if (!target) {
        try {
          target = $(href);
        } catch {
          target = null;
        }
      }
      if (!target) return;

      e.preventDefault();
      smoothScrollTo(target);
      setActiveTocLink(href);

      try {
        // Replace if already there, otherwise push (avoids endless back-stack spam)
        if (href === window.location.hash) history.replaceState(null, "", href);
        else history.pushState(null, "", href);
      } catch { /* no-op */ }
    };
    on(list, "click", list.__etTocClickHandler);

    if (section._eta_tocHashHandler) off(window, "hashchange", section._eta_tocHashHandler);
    section._eta_tocHashHandler = () => setActiveTocLink(window.location.hash || "");
    on(window, "hashchange", section._eta_tocHashHandler);
    setActiveTocLink(window.location.hash || "");

    // Active heading highlight (IO)
    if (section._eta_tocIo) section._eta_tocIo.disconnect();
    let io;
    try {
      io = new IntersectionObserver(
        (entries) => {
          let best = null;
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            if (!best || en.intersectionRatio > best.intersectionRatio) best = en;
          }
          if (!best) return;

          setActiveTocLink(`#${best.target.id}`);
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: [0.01, 0.2, 0.6, 1] }
      );
      headings.forEach((h) => io.observe(h));
    } catch {
      io = null;
    }
    section._eta_tocIo = io;

    if (!section._eta_tocCleanupBound) {
      section._eta_tocCleanupBound = true;
      (section._eta_cleanup ||= []).push(() => {
        if (section._eta_tocHashHandler) off(window, "hashchange", section._eta_tocHashHandler);
        if (section._eta_tocIo) section._eta_tocIo.disconnect();
        const cleanupList = $(".et-toc-list", section);
        if (cleanupList?.__etTocClickHandler) off(cleanupList, "click", cleanupList.__etTocClickHandler);
      });
    }

    section.dispatchEvent(new CustomEvent("eta:tocGenerated", { bubbles: true }));
  }

  /* =========================================================================
   * Tabs (no fixed height; robust a11y)
   * ========================================================================= */
  function initTabs(section) {
    const wrap = $("[data-tabs]", section);
    if (!wrap) return;

    const tabs = $$('[role="tab"]', wrap);
    if (!tabs.length) return;

    const getPanelFor = (btn) => {
      const id = btn?.getAttribute("aria-controls");
      return id ? document.getElementById(id) : null;
    };

    const activate = (index) => {
      tabs.forEach((btn, i) => {
        const selected = i === index;
        btn.setAttribute("aria-selected", selected ? "true" : "false");
        btn.tabIndex = selected ? 0 : -1;

        const panel = getPanelFor(btn);
        if (panel) selected ? panel.removeAttribute("hidden") : panel.setAttribute("hidden", "");
      });

      wrap.dispatchEvent(new CustomEvent("eta:tabChange", { detail: { index }, bubbles: true }));
    };

    // Click activation
    tabs.forEach((btn, i) => on(btn, "click", () => activate(i)));

    // Keyboard (left/right/home/end)
    tabs.forEach((btn, i) => {
      on(btn, "keydown", (e) => {
        const key = e.key;
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
        e.preventDefault();

        let next = i;
        if (key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
        if (key === "ArrowRight") next = (i + 1) % tabs.length;
        if (key === "Home") next = 0;
        if (key === "End") next = tabs.length - 1;

        tabs[next]?.focus();
        activate(next);
      });
    });

    // Ensure clean state (some editors/plugins may leave inline styles)
    wrap.style.removeProperty("height");
    wrap.style.removeProperty("min-height");
    wrap.style.overflow = "visible";

    activate(0);
  }

  /* =========================================================================
   * Reading progress (rAF scroll)
   * ========================================================================= */
  function initProgress(section) {
    const bar = $(".et-progress__bar", section);
    const body = $(".et-article-body", section);
    if (!bar || !body) return;

    const getY = () =>
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    let ticking = false;
    function update() {
      ticking = false;

      const bodyRect = body.getBoundingClientRect();
      const docTop = getY();
      const start = docTop + bodyRect.top;

      const total = Math.max((start + body.offsetHeight) - (docTop + window.innerHeight), 0);
      const scrolled = Math.min(Math.max(docTop - start, 0), total);

      bar.style.width = total > 0 ? `${(scrolled / total) * 100}%` : "0%";
    }

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    // Recompute when viewport geometry changes (mobile rotate / resize / late image layout).
    const onResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    on(window, "scroll", onScroll, { passive: true });
    on(window, "resize", onResize, { passive: true });
    on(window, "load", onResize);
    update();

    (section._eta_cleanup ||= []).push(() => {
      off(window, "scroll", onScroll);
      off(window, "resize", onResize);
      off(window, "load", onResize);
    });
  }

  /* =========================================================================
   * Heading copy-link anchors (excludes FAQ containers)
   * ========================================================================= */
  function initHeadingAnchors(section) {
    const body = $(".et-article-body", section);
    if (!body) return;

    const headings = ensureHeadingIds(body);
    if (!headings.length) return;

    headings.forEach((h) => {
      // Avoid duplicates
      if (h.querySelector(".et-anchor")) return;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "et-anchor";
      btn.setAttribute("aria-label", "Copy link to this section");
      btn.innerHTML = "🔗";

      on(btn, "click", async () => {
        const url = `${location.origin}${location.pathname}#${h.id}`;
        if (await copyToClipboard(url)) {
          announce("Section link copied");
          publishEt("anchor_copied", { anchor: h.id, context: { namespace: "blog_article", location: "heading_anchor" } });

          // Temporary visual confirmation (no CSS changes; swaps glyph)
          const prev = btn.innerHTML;
          const prevLabel = btn.getAttribute("aria-label");
          btn.innerHTML = "✓";
          btn.setAttribute("aria-label", "Copied");
          setTimeout(() => {
            btn.innerHTML = prev;
            btn.setAttribute("aria-label", prevLabel);
          }, 1200);
        }
      });

      h.prepend(btn);
    });
  }

  /* =========================================================================
   * Quote-to-share (lazy-create on first selection)
   * ========================================================================= */
  function initQuoteShare(section) {
    // If you decide to disable this feature later, gate it here (data-attr or setting).
    let pill = null;
    let hideTO = null;

    const isEditable = (el) =>
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

    const selText = () => (window.getSelection?.()?.toString() || "").trim();

    const ensurePill = () => {
      if (pill) return pill;

      pill = document.createElement("div");
      pill.className = "et-share-quote";
      pill.style.display = "none";
      pill.innerHTML = `
        <button data-act="x" aria-label="Post on X">Post</button>
        <button data-act="copy" aria-label="Copy quote">Copy</button>
      `;
      document.body.appendChild(pill);

      on(pill, "click", async (e) => {
        const act = e.target?.dataset?.act;
        const txt = selText();
        if (!txt) return hide();

        if (act === "copy") {
          const payload = `"${txt}" — ${document.title} ${location.href}`;
          if (await copyToClipboard(payload)) {
            announce("Quote copied");
            publishEt("quote_copied", { quote_len: txt.length, context: { namespace: "blog_article", location: "quote_share" } });

            pill.textContent = "Copied!";
            clearTimeout(hideTO);
            hideTO = setTimeout(hide, 900);
          }
        } else if (act === "x") {
          const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`"${txt}" — ${document.title}`)}&url=${encodeURIComponent(location.href)}`;
          publishEt("quote_share_x", { quote_len: txt.length, context: { namespace: "blog_article", location: "quote_share" } });
          window.open(url, "_blank", "noopener,noreferrer");
        }
      });

      return pill;
    };

    const show = () => {
      const el = ensurePill();
      el.style.display = "flex";
      clearTimeout(hideTO);
      hideTO = setTimeout(hide, 4000);
    };

    const hide = () => {
      if (!pill) return;
      pill.style.display = "none";
      pill.innerHTML = `
        <button data-act="x" aria-label="Post on X">Post</button>
        <button data-act="copy" aria-label="Copy quote">Copy</button>
      `;
    };

    const onMouseUp = (e) => {
      if (isEditable(e.target)) return hide();
      const t = selText();
      if (t) show();
      else hide();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") hide();
    };

    on(document, "mouseup", onMouseUp, { passive: true });
    on(document, "keydown", onKeyDown);

    (section._eta_cleanup ||= []).push(() => {
      clearTimeout(hideTO);
      off(document, "mouseup", onMouseUp);
      off(document, "keydown", onKeyDown);
      pill?.remove();
    });
  }

  /* =========================================================================
   * Read time
   * ========================================================================= */
  function initReadTime(section) {
    const slot = $("[data-read-time]", section);
    const body = $(".et-article-body", section);
    if (!slot || !body) return;

    const wpm = Math.min(300, Math.max(120, parseInt(slot.getAttribute("data-reading-speed"), 10) || 200));
    const words = (body.textContent || "").trim().match(/\S+/g)?.length || 0;
    const mins = Math.max(1, Math.round(words / wpm));

    slot.textContent = `${mins} min read`;
  }

  /* =========================================================================
   * Table normalization
   * - Enforces a single supported article table pattern:
   *   .table-wrap > table.compare
   * - Idempotent: safe to run on init + late DOM injections
   * ========================================================================= */
  function normalizeArticleTables(rootLike) {
    const scope = rootLike && rootLike.nodeType === 1 ? rootLike : document;
    const tables = $$(".et-article--pro .et-article-body table", scope);
    if (!tables.length) return;

    tables.forEach((table) => {
      if (!table.classList.contains("compare")) table.classList.add("compare");

      // If this table is already wrapped correctly, keep it.
      if (table.closest(".table-wrap")) return;

      const wrap = document.createElement("div");
      wrap.className = "table-wrap";
      table.parentNode?.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* =========================================================================
   * Share-row copy feedback
   * ========================================================================= */
  function initCopyFeedback(section) {
    const btns = $$("[data-copy-url], [data-copy-text]", section);
    if (!btns.length) return;

    const checkIcon = `
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"/>
      </svg>`;

    btns.forEach((btn) => {
      on(btn, "click", async (e) => {
        e.preventDefault();
        const text = btn.dataset.copyUrl || btn.dataset.copyText || window.location.href;
        if (!(await copyToClipboard(text))) return;

        announce("Link copied");
        publishEt("copy_link", { context: { namespace: "blog_article", location: "share_row" } });

        const originalHTML = btn.innerHTML;
        const originalLabel = btn.getAttribute("aria-label") || "Copy link";
        btn.innerHTML = checkIcon;
        btn.setAttribute("aria-label", "Copied!");
        btn.classList.add("is-copied");
        btn.disabled = true;

        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.setAttribute("aria-label", originalLabel);
          btn.classList.remove("is-copied");
          btn.disabled = false;
        }, 1400);
      });
    });
  }

  /* =========================================================================
   * Shop hide flag parity
   * ========================================================================= */
  function applyShopHide(section) {
    const hide = section?.dataset?.shopHide === "true";
    document.body.classList.toggle("eta-shop-hidden", !!hide);
  }

  /* =========================================================================
   * FAQ — Legacy upgrader (old “FAQ” heading patterns → .faq-accordion)
   * ========================================================================= */
  function upgradeLegacyFAQ(rootLike) {
    const scope = rootLike && rootLike.nodeType === 1 ? rootLike : document;

    // If standard FAQ already exists, we’re done.
    if (scope.querySelector(".faq-accordion")) return;

    // Find H2 “FAQ” robustly
    const faqH2 = [...scope.querySelectorAll("h2")].find((h) => isFAQHeadingText(h.textContent)) || null;
    if (!faqH2) return;

    const root =
      faqH2.closest(".et-article-body, .et-article--pro, .et-article, .rte, article, main") || document;

    if (root.hasAttribute("data-faq-upgraded")) return;

    // Collect following elements until next H2 (robust to wrappers)
    const collected = [];
    for (let el = faqH2.nextElementSibling; el; el = el.nextElementSibling) {
      if (el.tagName === "H2") break;
      collected.push(el);
    }
    if (!collected.length) return;

    const isStrongQ = (el) => {
      if (!el || el.tagName !== "P") return false;
      const s = el.firstElementChild;
      if (!s || s.tagName !== "STRONG") return false;
      const t = (s.textContent || "").trim();
      return t.length > 0 && /[?؟]$/.test(t);
    };

    // Build accordion container
    const wrap = document.createElement("div");
    wrap.className = "faq-accordion";
    wrap.setAttribute("data-et-faq", "1");

    let i = 0;
    let built = 0;

    while (i < collected.length) {
      const node = collected[i];
      let qText = null;

      if (node.tagName === "H3") qText = (node.textContent || "").trim();
      else if (isStrongQ(node)) qText = (node.querySelector("strong")?.textContent || "").trim();

      if (!qText) { i++; continue; }
      i++;

      // Collect answer nodes until next question marker
      const answerNodes = [];
      while (i < collected.length) {
        const n2 = collected[i];
        if (n2.tagName === "H3" || isStrongQ(n2)) break;
        answerNodes.push(n2.cloneNode(true));
        i++;
      }

      const details = document.createElement("details");
      const summary = document.createElement("summary");
      summary.textContent = qText;

      const answerWrap = document.createElement("div");
      // Keep answers lightweight; allow existing markup
      answerNodes.forEach((n) => answerWrap.appendChild(n));

      details.appendChild(summary);
      details.appendChild(answerWrap);
      wrap.appendChild(details);
      built++;
    }

    if (!built) return;

    // Insert before legacy FAQ heading and remove legacy block
    faqH2.parentNode.insertBefore(wrap, faqH2);

    // Remove legacy H2 + collected nodes
    faqH2.remove();
    collected.forEach((n) => n.parentNode && n.parentNode.removeChild(n));

    root.setAttribute("data-faq-upgraded", "1");

    publishEt("faq_upgraded", { context: { namespace: "blog_article", location: "faq" } });

    // Notify internal listeners (e.g., rebinding)
    try {
      wrap.dispatchEvent(new CustomEvent("et:faq_upgraded", { bubbles: true }));
    } catch { /* no-op */ }
  }

  /* =========================================================================
   * FAQ interaction shim
   * ========================================================================= */
  function bindFAQInteractions(rootLike) {
    const scope = rootLike && rootLike.nodeType === 1 ? rootLike : document;

    const details = $$(".faq-accordion details, details.et-faq__item", scope);
    if (!details.length) return;

    details.forEach((d, idx) => {
      if (d.__etBound) return;
      d.__etBound = true;

      const s = d.querySelector("summary");
      if (!s) return;

      if (!s.hasAttribute("tabindex")) s.setAttribute("tabindex", "0");
      s.style.pointerEvents = "auto";

      // Force toggle only if native didn’t toggle
      function toggleDetails() {
        const wasOpen = d.open;
        setTimeout(() => {
          if (d.open !== wasOpen) return; // native worked
          d.open = !wasOpen;
          try {
            d.dispatchEvent(new Event("toggle", { bubbles: true }));
          } catch { /* no-op */ }
        }, 0);
      }

      s.addEventListener("click", toggleDetails, false);
      s.addEventListener("keydown", (ev) => {
        const key = ev.key || ev.code;
        if (key === "Enter" || key === " " || key === "Spacebar") {
          ev.preventDefault();
          toggleDetails();
        }
      }, true);

      d.addEventListener("toggle", () => {
        const title = (s.textContent || `Q${idx + 1}`).trim();
        publishEt("faq_toggled", {
          state: d.open ? "open" : "close",
          title,
          context: { namespace: "blog_article", location: "faq" },
        });
      });
    });
  }

  /* =========================================================================
   * Related Articles (hydrated client-side)
   * ========================================================================= */
  function parsePinnedRelated(container) {
    const script = $('[data-et-related-pinned]', container);
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent || "[]");
      if (!Array.isArray(data) || !data.length) return null;
      return data
        .map((x) => ({
          title: typeof x.title === "string" ? x.title : "",
          url: typeof x.url === "string" ? x.url : "",
        }))
        .filter((x) => x.title && x.url);
    } catch {
      return null;
    }
  }

  function chooseRelatedQuery() {
    const a = getArticle();
    const topic = (a.primary_topic || "").toString().trim();
    const tags = Array.isArray(a.tags) ? a.tags.map(String) : [];

    // Prefer explicit mapping (stable)
    if (topic) return topic;

    // Otherwise pick the first "meaningful" tag (skip very generic ones)
    const blacklist = new Set(["featured", "latest", "general", "blog", "news"]);
    const pick = tags.find((t) => {
      const k = String(t || "").toLowerCase().trim();
      if (!k) return false;
      if (blacklist.has(k)) return false;
      if (k.length < 3) return false;
      return true;
    });

    return pick || "wellness";
  }

  function normalizeToRelative(url) {
    try {
      const u = new URL(url, window.location.origin);
      return u.pathname + u.search + u.hash;
    } catch {
      return url;
    }
  }

  // Yield expensive related hydration until the browser is idle when possible.
  function scheduleIdle(task) {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => task(), { timeout: 1200 });
    } else {
      setTimeout(task, 120);
    }
  }

  /* -------------------------------------------------------------------------
   * NEW (targeted): Related Articles enrichment (thumbnail + excerpt)
   * - Fetches each related article HTML (top 4) and extracts:
   *   - og:image / twitter:image
   *   - meta description / og:description
   * - Cached in sessionStorage (24h) to avoid repeat fetches
   * ---------------------------------------------------------------------- */
  function cacheKeyForArticle(url) {
    return `et_related_meta::${normalizeToRelative(url)}`;
  }

  function getCachedArticleMeta(url) {
    try {
      const raw = sessionStorage.getItem(cacheKeyForArticle(url));
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data && data.t && (Date.now() - data.t) < 86400000) return data;
    } catch { /* no-op */ }
    return null;
  }

  function setCachedArticleMeta(url, meta) {
    try {
      sessionStorage.setItem(cacheKeyForArticle(url), JSON.stringify({ t: Date.now(), ...meta }));
    } catch { /* no-op */ }
  }

  function extractMetaFromHtml(html, fallbackTitle) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const ogImg = doc.querySelector('meta[property="og:image"], meta[name="og:image"]')?.getAttribute("content") || "";
    const twitterImg = doc.querySelector('meta[name="twitter:image"]')?.getAttribute("content") || "";
    const image = (ogImg || twitterImg || "").trim();

    const metaDesc = doc.querySelector('meta[name="description"]')?.getAttribute("content") || "";
    const ogDesc = doc.querySelector('meta[property="og:description"]')?.getAttribute("content") || "";
    const excerpt = (metaDesc || ogDesc || "").trim();

    const title = (doc.querySelector("title")?.textContent || fallbackTitle || "").trim();

    return { title, image, excerpt };
  }

  async function fetchArticleMeta(url, fallbackTitle) {
    const cached = getCachedArticleMeta(url);
    if (cached) return cached;

    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) return { title: fallbackTitle, image: "", excerpt: "" };

    const html = await res.text();
    const meta = extractMetaFromHtml(html, fallbackTitle);
    setCachedArticleMeta(url, meta);
    return meta;
  }

  async function enrichRelatedItems(items, limit = 4) {
    const top = (items || []).slice(0, limit);

    const enriched = await Promise.all(top.map(async (it) => {
      try {
        const meta = await fetchArticleMeta(it.url, it.title);
        return {
          title: it.title || meta.title,
          url: it.url,
          image: meta.image || "",
          excerpt: meta.excerpt || ""
        };
      } catch {
        return { title: it.title, url: it.url, image: "", excerpt: "" };
      }
    }));

    return enriched;
  }

  /* -------------------------------------------------------------------------
   * UPDATED: renderRelated() now supports optional thumbnail + excerpt.
   * Everything else is unchanged.
   * ---------------------------------------------------------------------- */
  function renderRelated(container, items) {
    const list = container.querySelector('[data-et-related-list]');
    if (!list) return;

    const currentUrl = container.getAttribute("data-et-related-current-url") || "";
    const currentRel = normalizeToRelative(currentUrl);

    const limit = Math.max(
      1,
      Math.min(8, parseInt(container.getAttribute("data-et-related-limit"), 10) || 4)
    );

    const filtered = (items || [])
      .map((x) => ({
        title: x.title,
        url: normalizeToRelative(x.url),
        image: x.image || "",
        excerpt: x.excerpt || ""
      }))
      .filter((x) => x.title && x.url && x.url !== currentRel)
      .slice(0, limit);

    // --- EMPTY STATE: hide entire module ---
    if (!filtered.length) {
      list.innerHTML = "";
      container.setAttribute("data-et-related-empty", "1");
      container.hidden = true; // hides heading + list
      return;
    }

    // --- SUCCESS: render and ensure visible ---
    list.innerHTML = filtered
      .map((x) => {
        const thumb = x.image
          ? `<span class="related-article-thumb" aria-hidden="true"><img src="${escapeHtml(x.image)}" alt="" loading="lazy" decoding="async"></span>`
          : ``;

        const excerpt = x.excerpt
          ? `<span class="related-article-excerpt">${escapeHtml(x.excerpt)}</span>`
          : ``;

        return `
          <li class="related-article-item">
            <a href="${escapeHtml(x.url)}" class="related-article-link" tabindex="0"
               data-block-type="related_article"
               data-article-id="${escapeHtml(String(getArticleId() || ""))}">
              <span class="related-article-meta">
                ${thumb}
                <span class="related-article-text">
                  <span class="related-article-title">${escapeHtml(x.title)}</span>
                  ${excerpt}
                </span>
              </span>
            </a>
          </li>`;
      })
      .join("");

    container.hidden = false; // ensure visible when results exist
    container.removeAttribute("data-et-related-empty");

    publishEt("related_rendered", {
      count: filtered.length,
      source: container.__etRelatedSource || "unknown",
      context: { namespace: "blog_article", location: "related_articles" },
    });
  }

  async function fetchPredictiveArticles(query, limit = 8) {
    // Predictive search endpoint (Shopify):
    // /search/suggest.json?q=...&resources[type]=article&resources[limit]=...
    const q = encodeURIComponent(query || "");
    const url = `/search/suggest.json?q=${q}&resources[type]=article&resources[limit]=${encodeURIComponent(String(limit))}&resources[options][unavailable_products]=hide`;

    const res = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    });

    if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);
    const json = await res.json();

    const results = json?.resources?.results?.articles;
    if (!Array.isArray(results)) return [];

    // Shopify returns objects like { title, url, ... }
    return results
      .map((a) => ({
        title: a?.title || "",
        url: a?.url || "",
      }))
      .filter((x) => x.title && x.url);
  }

  async function hydrateRelatedArticles(section, options = {}) {
    const container = $('[data-et-related]', section) || $('[data-et-related]');
    if (!container) return;
    if (container.__etHydrating) return;
    if (container.__etHydrated && !options.force) return;
    container.__etHydrating = true;

    try {
      // 1) Pinned list wins (editor-deterministic)
      const pinned = parsePinnedRelated(container);
      if (pinned && pinned.length) {
        container.__etRelatedSource = "pinned";
        if (canEagerNetwork()) {
          const enriched = await enrichRelatedItems(pinned, 4);
          renderRelated(container, enriched);
        } else {
          renderRelated(container, pinned);
        }
        return;
      }

      // 2) Predictive search (skip on slow links/save-data)
      if (!canEagerNetwork()) {
        container.__etRelatedSource = "skipped_slow_network";
        renderRelated(container, []); // no UI change; just empty
        return;
      }

      const query = chooseRelatedQuery();
      try {
        const items = await fetchPredictiveArticles(query, 10);
        container.__etRelatedSource = "predictive_search";
        const enriched = await enrichRelatedItems(items, 4);
        renderRelated(container, enriched);
      } catch {
        container.__etRelatedSource = "predictive_error";
        renderRelated(container, []);
      }
    } finally {
      container.__etHydrating = false;
      container.__etHydrated = true;
    }
  }

  function initRelatedArticles(section) {
    const container = $('[data-et-related]', section) || $('[data-et-related]');
    if (!container || container.__etLazyBound) return;
    container.__etLazyBound = true;

    const hydrate = () => scheduleIdle(() => hydrateRelatedArticles(section));

    // Keep related module off the critical path: hydrate near viewport.
    try {
      const io = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((en) => en.isIntersecting)) return;
          observer.disconnect();
          hydrate();
        },
        { rootMargin: "320px 0px", threshold: 0.01 }
      );
      io.observe(container);
      (section._eta_cleanup ||= []).push(() => io.disconnect());
    } catch {
      hydrate();
    }
  }

  /* =========================================================================
   * “Viewed Blog” tracking (Klaviyo + et_* echo)
   * - idempotent (once per page load)
   * ========================================================================= */
  function isBlogArticle() {
    // Prefer explicit signal
    if (getET()?.page === "blog") return true;
    // Fallbacks
    if ($('.et-article[data-et-page="blog"]')) return true;
    if (document.body?.classList?.contains?.("template-article")) return true;
    return false;
  }

  function emitKlaviyoViewed(meta) {
    // Defer until Klaviyo is ready; minimal retries, then fail silently.
    let tries = 0;
    const maxTries = 20; // ~2s at 100ms
    const tick = () => {
      if (window._learnq && typeof window._learnq.push === "function") {
        window._learnq.push(["track", "Viewed Blog", meta]);
        return;
      }
      if (++tries < maxTries) setTimeout(tick, 100);
    };
    tick();
  }

  function trackViewedBlogOnce() {
    if (!isBlogArticle()) return;
    if (!once("et_viewed_blog_sent")) return;

    const a = getArticle();
    const meta = {
      article_id: a.id,
      article_title: a.title || document.title || "",
      article_handle: a.handle || "",
      article_url: a.url || location.href,
      primary_topic: (a.primary_topic || "").toString().toLowerCase() || "other",
      tags: Array.isArray(a.tags) ? a.tags : [],
    };

    // Klaviyo behavioural trigger
    emitKlaviyoViewed(meta);

    // Unified et_* analytics
    publishEt("viewed_blog", {
      article_title: meta.article_title,
      article_handle: meta.article_handle,
      article_url: meta.article_url,
      primary_topic: meta.primary_topic,
      tags: meta.tags,
      context: { namespace: "blog_article", location: "view" },
    });
  }

  /* =========================================================================
   * Single MutationObserver (debounced work queue)
   * - Prevent observer soup
   * - Handles late-injected content (apps/editor)
   * ========================================================================= */
  const queue = (() => {
    let raf = 0;
    let lastRun = 0;

    // Backoff guard so we don’t thrash on noisy DOM changes
    const MIN_GAP_MS = 120;

    return {
      schedule(fn) {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const t = nowMs();
          if (t - lastRun < MIN_GAP_MS) return;
          lastRun = t;
          try { fn(); } catch { /* no-op */ }
        });
      },
    };
  })();

  function initObserver(section) {
    if (document.__etMO) return; // single global observer
    try {
      const mo = new MutationObserver(() => {
        queue.schedule(() => {
          normalizeArticleTables(document);

          // Late-content FAQ upgrade/bind
          upgradeLegacyFAQ(document);
          bindFAQInteractions(document);

          // Strip anchors that accidentally land inside FAQ UI (defensive)
          stripFAQAnchors(document);

          // Sanitize FAQ question text (defensive)
          sanitizeFAQQuestionText(document);
        });
      });

      mo.observe(document.body, { childList: true, subtree: true });
      document.__etMO = mo;
    } catch {
      document.__etMO = null;
    }
  }

  /* =========================================================================
   * Defensive FAQ anchor stripping
   * ========================================================================= */
  function stripFAQAnchors(root = document) {
    // Remove any heading “copy link” buttons inside FAQ UI
    root
      .querySelectorAll('.faq-accordion .et-anchor, .et-faq .et-anchor, .faq-accordion [aria-label="Copy link to this section"], .et-faq [aria-label="Copy link to this section"]')
      .forEach((n) => n.remove());
  }

  /* =========================================================================
   * FAQ question text sanitizer (removes emoji/pictograph “icons”)
   * ========================================================================= */
  const FAQ_STRIP = /\p{Extended_Pictographic}|\uFE0F|[📎🔗🖇️★☆•▪︎►▸▹▷▻▶❯›»]/gu;

  function cleanFAQNode(n) {
    const before = n.textContent || "";
    const after = before
      .normalize("NFKC")
      .replace(FAQ_STRIP, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    if (after !== before) n.textContent = after;
  }

  function sanitizeFAQQuestionText(root = document) {
    // Standard accordion summaries
    root.querySelectorAll(".faq-accordion summary").forEach(cleanFAQNode);
    // Legacy/other containers
    root.querySelectorAll(".et-faq__q-text").forEach(cleanFAQNode);
  }

  /* =========================================================================
   * Micro summary enhancer
   * - Upgrades existing article MS_GEO blocks into the same closed accordion
   *   pattern used on the new hub pages without requiring content rewrites.
   * ========================================================================= */
  function enhanceMicroSummary(root = document) {
    root.querySelectorAll(".et-article .micro-summary, .blog-post .micro-summary").forEach((card, index) => {
      if (card.dataset.msGeoAccordionReady === "true") return;
      if (card.closest(".et-vitamins-hub")) return;
      if (card.classList.contains("micro-summary--pdp")) return;

      const heading = card.querySelector("h2");
      if (!heading) return;

      const details = document.createElement("details");
      details.className = `${card.className} micro-summary--accordion`;

      Array.from(card.attributes).forEach((attr) => {
        if (attr.name === "class") return;
        details.setAttribute(attr.name, attr.value);
      });

      const summary = document.createElement("summary");
      summary.className = "micro-summary__summary";

      const summaryCopy = document.createElement("div");
      summaryCopy.className = "micro-summary__summary-copy";

      const eyebrow = document.createElement("p");
      eyebrow.className = "micro-summary__eyebrow";
      eyebrow.textContent = "Quick orientation";

      const title = document.createElement("span");
      title.className = "micro-summary__title";
      title.id = heading.id || `article-micro-summary-${index + 1}`;
      title.setAttribute("role", "heading");
      title.setAttribute("aria-level", "2");
      const headingText = (heading.textContent || "")
        .replace(/^[\s#📎🔗]+/u, "")
        .trim();
      title.textContent = headingText || "Key takeaways at a glance";
      Array.from(heading.attributes).forEach((attr) => {
        if (attr.name === "id" || attr.name === "class") return;
        title.setAttribute(attr.name, attr.value);
      });

      const toggle = document.createElement("span");
      toggle.className = "micro-summary__toggle";
      toggle.setAttribute("aria-hidden", "true");

      summaryCopy.append(eyebrow, title);
      summary.append(summaryCopy, toggle);

      const content = document.createElement("div");
      content.className = "micro-summary__content";

      Array.from(card.childNodes).forEach((node) => {
        if (node === heading) return;
        content.appendChild(node);
      });

      details.append(summary, content);
      details.dataset.msGeoAccordionReady = "true";
      card.replaceWith(details);
    });
  }

  /* =========================================================================
   * Section lifecycle
   * ========================================================================= */
  function initSection(section) {
    if (!section || section._eta_initialized) return;
    section._eta_initialized = true;

    normalizeArticleTables(section);

    // Core behaviour (no visual changes)
    buildTOC(section);
    initTabs(section);
    initProgress(section);
    initHeadingAnchors(section);
    initQuoteShare(section);
    initReadTime(section);
    initCopyFeedback(section);
    applyShopHide(section);

    // FAQ robustness
    upgradeLegacyFAQ(document);
    bindFAQInteractions(document);
    stripFAQAnchors(document);
    sanitizeFAQQuestionText(document);
    enhanceMicroSummary(document);

    // Related articles hydration
    initRelatedArticles(section);

    // Analytics (page-level)
    trackViewedBlogOnce();
    trackImpressionsOnce();
    trackClickDelegated();

    // Single global observer for late injections
    initObserver(section);

    // Editor lifecycle hooks (recompute TOC etc.)
    const recompute = () => {
      normalizeArticleTables(section);
      buildTOC(section);
      // Rebind FAQ if theme editor injects content
      upgradeLegacyFAQ(document);
      bindFAQInteractions(document);
      stripFAQAnchors(document);
      sanitizeFAQQuestionText(document);
      enhanceMicroSummary(document);
      initRelatedArticles(section);
    };

    on(document, "shopify:block:select", recompute);
    on(document, "shopify:block:deselect", recompute);

    (section._eta_cleanup ||= []).push(() => {
      off(document, "shopify:block:select", recompute);
      off(document, "shopify:block:deselect", recompute);
    });
  }

  function teardownSection(section) {
    if (!section || !section._eta_initialized) return;
    section._eta_initialized = false;

    const stack = section._eta_cleanup || [];
    for (let i = 0; i < stack.length; i++) {
      try { stack[i](); } catch { /* no-op */ }
    }
    section._eta_cleanup = [];
  }

  function boot(root = document) {
    const sections = $$(".et-article", root);

    if (sections.length) {
      sections.forEach(initSection);
    } else {
      // Legacy templates without .et-article wrapper
      normalizeArticleTables(root);
      upgradeLegacyFAQ(root);
      bindFAQInteractions(root);
      stripFAQAnchors(root);
      sanitizeFAQQuestionText(root);
      enhanceMicroSummary(root);
      trackViewedBlogOnce();
      trackImpressionsOnce();
      trackClickDelegated();
      initRelatedArticles(document);
      initObserver(document);
    }
  }

  // Boot
  if (!isBrowser) return;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(), { once: true });
  } else {
    boot();
  }

  // Shopify section lifecycle
  document.addEventListener("shopify:section:load", (e) => boot(e.target));
  document.addEventListener("shopify:section:unload", (e) => teardownSection($(".et-article", e.target)));
})();
